import { InteractivityChecker } from "@angular/cdk/a11y";
import { hasModifierKey } from "@angular/cdk/keycodes";
import { Directive, HostListener, inject } from "@angular/core";

/** Marks the primary navigation; the value is its layout axis ("vertical" side menu, "horizontal" bottom bar). */
export const PRIMARY_NAV_ATTR = "data-primary-nav";
/** Marks the container the routed page renders into. */
export const PRIMARY_CONTENT_ATTR = "data-primary-content";

const NAV_ITEM_SELECTOR = "a[href], button:not([disabled])";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "[tabindex]",
  "[contenteditable]:not([contenteditable='false'])",
].join(",");

/**
 * Widgets that already own the horizontal arrow keys (caret movement, tabs, radios, grids,
 * sliders, …). Left inside one of these must keep its own meaning instead of jumping to the menu.
 * A modal rendered inside the page (appModalOverlay) is included: it traps focus, and jumping to
 * the menu would escape it.
 */
const OWNS_ARROW_KEYS_SELECTOR = [
  "[aria-modal='true']",
  "input",
  "select",
  "textarea",
  "[contenteditable]:not([contenteditable='false'])",
  "[role='tablist']",
  "[role='radiogroup']",
  "[role='listbox']",
  "[role='menu']",
  "[role='menubar']",
  "[role='grid']",
  "[role='treegrid']",
  "[role='tree']",
  "[role='toolbar']",
  "[role='slider']",
  "[role='spinbutton']",
  "[role='combobox']",
  "[aria-orientation='horizontal']",
  ".ag-root-wrapper",
].join(",");

/**
 * Directional navigation between the primary menu and the routed page:
 * - Vertical (side) menu: Up/Down/Home/End move between items, and Right Arrow moves focus to
 *   the first tabbable control in the page content.
 * - Horizontal (bottom) bar: Left/Right/Home/End move between items - Right is taken by the bar's
 *   own axis, so it has no "into the page" key; Tab gets there.
 * - Left Arrow inside the page content moves focus back to the active menu item, except where
 *   Left already scrolls something sideways, or inside an overlay layered over the page.
 *
 * Put it on an ancestor of both regions; mark the nav with `data-primary-nav` and the content
 * root with `data-primary-content`. Keys a descendant already handled (defaultPrevented), keys
 * with modifiers (Alt+Left is browser Back) and keys inside widgets that own their arrows are
 * left alone. Tab / Shift+Tab order is unchanged.
 */
@Directive({
  selector: "[appPrimaryNavArrowKeys]",
  standalone: true,
})
export class PrimaryNavArrowKeysDirective {
  private readonly checker = inject(InteractivityChecker);

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || hasModifierKey(event)) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const host = event.currentTarget as HTMLElement;
    const nav = target.closest<HTMLElement>(`[${PRIMARY_NAV_ATTR}]`);
    if (nav && host.contains(nav)) {
      this.handleNavKey(event, nav, host);
      return;
    }

    const content = target.closest<HTMLElement>(`[${PRIMARY_CONTENT_ATTR}]`);
    if (content && host.contains(content) && event.key === "ArrowLeft") {
      if (target.closest(OWNS_ARROW_KEYS_SELECTOR) || keepsLeftArrow(target, content)) {
        return;
      }
      const navTarget = this.activeNavItem(host);
      if (navTarget) {
        event.preventDefault();
        navTarget.focus();
      }
    }
  }

  private handleNavKey(event: KeyboardEvent, nav: HTMLElement, host: HTMLElement): void {
    const vertical = nav.getAttribute(PRIMARY_NAV_ATTR) === "vertical";
    if (vertical && event.key === "ArrowRight") {
      const content = host.querySelector<HTMLElement>(`[${PRIMARY_CONTENT_ATTR}]`);
      const first = content && this.firstTabbable(content);
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    const [prevKey, nextKey] = vertical ? ["ArrowUp", "ArrowDown"] : ["ArrowLeft", "ArrowRight"];
    const items = this.navItems(nav);
    const current = items.findIndex((item) => item.contains(event.target as Node));
    if (current < 0 || items.length === 0) {
      return;
    }
    let next: number;
    switch (event.key) {
      case nextKey:
        next = (current + 1) % items.length;
        break;
      case prevKey:
        next = (current - 1 + items.length) % items.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    items[next].focus();
  }

  private navItems(nav: HTMLElement): HTMLElement[] {
    return Array.from(nav.querySelectorAll<HTMLElement>(NAV_ITEM_SELECTOR)).filter((el) =>
      this.checker.isFocusable(el),
    );
  }

  /** The current page's menu item, falling back to the first item when the page isn't in the menu. */
  private activeNavItem(host: HTMLElement): HTMLElement | null {
    for (const nav of Array.from(host.querySelectorAll<HTMLElement>(`[${PRIMARY_NAV_ATTR}]`))) {
      const items = this.navItems(nav);
      if (items.length) {
        return items.find((item) => item.getAttribute("aria-current") === "page") ?? items[0];
      }
    }
    return null;
  }

  private firstTabbable(root: HTMLElement): HTMLElement | null {
    for (const el of Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))) {
      if (el.closest("[inert]")) {
        continue;
      }
      if (this.checker.isFocusable(el) && this.checker.isTabbable(el)) {
        return el;
      }
    }
    return null;
  }
}

/**
 * Whether Left Arrow on `target` belongs to an ancestor between it and the page content: a
 * horizontally scrollable one (a wide table, a chip row) not already at its left edge, where the
 * browser's own scrolling wins; or a fixed-position overlay layered over the page (one without
 * aria-modal, e.g. Cabinet's upload form), where the menu it would jump to is hidden underneath.
 */
function keepsLeftArrow(target: HTMLElement, content: HTMLElement): boolean {
  for (let el: HTMLElement | null = target; el && el !== content; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.position === "fixed") {
      return true;
    }
    const scrollable = style.overflowX === "auto" || style.overflowX === "scroll";
    if (scrollable && el.scrollWidth > el.clientWidth && el.scrollLeft > 0) {
      return true;
    }
  }
  return false;
}
